/* eslint-disable react/forbid-dom-props */
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { User, Shield, Check, Mail, Phone, Cpu } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { metricsApi, usersApi } from '../../services/api';
import UserAvatar from '../../components/UserAvatar';

function cropAvatarFile(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const size = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 256, 256);
      canvas.toBlob(blob => {
        URL.revokeObjectURL(url);
        if (!blob) reject(new Error('Não foi possível processar a imagem.'));
        else resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.9);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Imagem inválida.'));
    };
    img.src = url;
  });
}

// Nomes técnicos de `task_type` não servem para quem usa o sistema: ninguém reconhece
// "petition_generation.classificador" como uma etapa do próprio trabalho.
const TAREFA_LABEL = {
  'chat': 'Conversa com a Cintra',
  'audit': 'Auditoria da ficha',
  'document_import': 'Importação de documento',
  'petition_generation.classificador': 'Geração de petição · classificação',
  'petition_generation.redator': 'Geração de petição · redação',
  'summary': 'Resumo e ensinamentos',
};

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function MeuConsumoIA({ userId }) {
  const inicioDoMes = new Date();
  inicioDoMes.setDate(1);
  const dateFrom = inicioDoMes.toISOString().slice(0, 10);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['llm-usage', 'self', userId, dateFrom],
    queryFn: () => metricsApi.getLlmUsage({ user_id: userId, date_from: dateFrom }),
    enabled: !!userId,
  });

  if (!userId) return null;

  return (
    <div className="card" style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '.5rem', color: 'var(--text-primary)' }}>
          <Cpu size={15} color="var(--gold)" />
          Meu consumo de IA neste mês
        </h2>
        <div style={{ borderBottom: '1px solid var(--border-light)', paddingBottom: '.25rem' }} />
      </div>

      {isLoading && <div className="spinner" style={{ width: 20, height: 20 }} />}
      {isError && (
        <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', margin: 0 }}>
          Não foi possível carregar seu consumo agora. Tente recarregar a página.
        </p>
      )}

      {data && data.total_calls === 0 && (
        <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', margin: 0 }}>
          Você ainda não usou nenhum recurso de IA neste mês. Gerar uma petição ou conversar com a
          Cintra passa a aparecer aqui.
        </p>
      )}

      {data && data.total_calls > 0 && (
        <>
          <div className="form-grid fg-3" style={{ gap: '1rem' }}>
            {[
              ['Chamadas', data.total_calls.toLocaleString('pt-BR')],
              ['Tokens enviados', (data.tokens_in || 0).toLocaleString('pt-BR')],
              ['Tokens recebidos', (data.tokens_out || 0).toLocaleString('pt-BR')],
            ].map(([label, valor]) => (
              <div key={label}>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>{valor}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{label}</div>
              </div>
            ))}
          </div>

          <div>
            <div style={{ fontSize: '.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: '.5rem' }}>
              Por tarefa
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
              {data.by_task_type.map(t => (
                <div key={t.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '.82rem', paddingBottom: '.35rem', borderBottom: '1px solid var(--border-light)' }}>
                  <span>{TAREFA_LABEL[t.key] || t.key}</span>
                  <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {t.count} {t.count === 1 ? 'chamada' : 'chamadas'} · {brl(t.custo_total)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem' }}>
            <span style={{ fontSize: '.82rem', fontWeight: 600 }}>Total estimado</span>
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{brl(data.custo_total)}</span>
          </div>
        </>
      )}

      <p style={{ fontSize: '.7rem', color: 'var(--text-muted)', margin: 0 }}>
        Valores são estimativas convertidas por uma taxa fixa de câmbio e podem divergir da fatura
        do provedor por variação cambial, arredondamento, cache e retentativas.
      </p>
    </div>
  );
}

function FG({ label, hint, children, style }) {
  return (
    <div className="form-group" style={style}>
      <label style={{ fontSize: '.78rem', fontWeight: 500 }}>{label}</label>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

export default function Perfil() {
  const { user, updateUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    signature_name: '',
    oab_sp: '',
    oab_mg: '',
    professional_email: '',
    professional_phone: '',
    is_lawyer: false,
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        whatsapp: user.whatsapp || '',
        signature_name: user.signature_name || '',
        oab_sp: user.oab_sp || '',
        oab_mg: user.oab_mg || '',
        professional_email: user.professional_email || '',
        professional_phone: user.professional_phone || '',
        is_lawyer: !!user.is_lawyer,
      });
    }
  }, [user]);

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setAvatarLoading(true);
    try {
      const cropped = await cropAvatarFile(file);
      await usersApi.uploadAvatar(user.id, cropped);
      updateUser({ has_avatar: true });
      toast.success('Foto de perfil atualizada.');
    } catch (err) {
      toast.error(err.message || 'Erro ao processar imagem.');
    } finally {
      setAvatarLoading(false);
      e.target.value = '';
    }
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('O nome é obrigatório.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        signature_name: form.signature_name.trim() || null,
        oab_sp: form.oab_sp.trim() || null,
        oab_mg: form.oab_mg.trim() || null,
        professional_email: form.professional_email.trim() || null,
        professional_phone: form.professional_phone.trim() || null,
        is_lawyer: form.is_lawyer,
      };
      const updated = await usersApi.updateMe(payload);
      updateUser(updated);
      toast.success('Perfil atualizado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar dados.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1>Meu Perfil</h1>
        <p style={{ marginTop: '.1rem' }}>Gerencie suas informações pessoais e credenciais de assinatura jurídica</p>
      </div>

      <div className="perfil-layout-grid" style={{ gap: '1.5rem', alignItems: 'start' }}>
        
        {/* Foto / Avatar Card */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', textAlign: 'center' }}>
          <div style={{ position: 'relative' }}>
            <UserAvatar user={user} size={110} />
            {avatarLoading && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,35,50,0.5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner" style={{ width: 22, height: 22 }} />
              </div>
            )}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{user?.name}</div>
            <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{user?.role_label || user?.role}</div>
          </div>
          
          <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer', width: '100%' }}>
            Alterar foto
            <input type="file" accept="image/png,image/jpeg" onChange={handleAvatarChange} style={{ display: 'none' }} disabled={avatarLoading} />
          </label>
          <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>
            Formatos suportados: JPEG, PNG. Limite de 2 MB.
          </div>
        </div>

        {/* Formulário Card */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '.5rem', color: 'var(--text-primary)' }}>
              <User size={15} color="var(--gold)" />
              Informações Pessoais
            </h2>
            <div style={{ borderBottom: '1px solid var(--border-light)', paddingBottom: '.25rem' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.875rem' }}>
            <FG label="Nome completo *" style={{ gridColumn: '1/-1' }}>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Erica Mendonça Cintra" />
            </FG>
            <FG label="E-mail de acesso">
              <input value={form.email} disabled style={{ background: 'var(--bg)', color: 'var(--text-muted)' }} />
            </FG>
            <FG label="Perfil / Cargo">
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', height: '100%', fontSize: '.85rem', color: 'var(--text-secondary)' }}>
                <Shield size={14} color="var(--gold)" />
                <strong>{user?.role_label || user?.role}</strong>
              </div>
            </FG>
            <FG label="Telefone pessoal">
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Ex: (11) 99999-9999" />
            </FG>
            <FG label="WhatsApp">
              <input value={form.whatsapp} onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="Ex: (11) 99999-9999" />
            </FG>
          </div>

          {/* Dados Profissionais */}
          <div style={{ marginTop: '.75rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '.5rem', color: 'var(--text-primary)' }}>
              <Shield size={15} color="var(--gold)" />
              Credenciais Profissionais e Assinatura
            </h2>
            <div style={{ borderBottom: '1px solid var(--border-light)', paddingBottom: '.25rem', marginBottom: '1rem' }} />
            
            <label className="check-group" style={{ marginBottom: '1.25rem' }}>
              <input type="checkbox" checked={form.is_lawyer} onChange={e => setForm(f => ({ ...f, is_lawyer: e.target.checked }))} />
              <div>
                <span className="check-label" style={{ fontWeight: 600 }}>Perfil profissional de Advogado</span>
                <span className="check-sub">Habilitar dados OAB e assinatura oficial para vinculação a casos</span>
              </div>
            </label>

            {form.is_lawyer && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.875rem', animation: 'fadeIn .2s ease' }}>
                <FG label="Nome de Assinatura" hint="Como seu nome deve aparecer no encerramento da petição" style={{ gridColumn: '1/-1' }}>
                  <input value={form.signature_name} onChange={e => setForm(f => ({ ...f, signature_name: e.target.value }))} placeholder="Ex: Erica M. Cintra" />
                </FG>
                <FG label="OAB / SP">
                  <input value={form.oab_sp} onChange={e => setForm(f => ({ ...f, oab_sp: e.target.value }))} placeholder="Ex: 123456" />
                </FG>
                <FG label="OAB / MG">
                  <input value={form.oab_mg} onChange={e => setForm(f => ({ ...f, oab_mg: e.target.value }))} placeholder="Ex: 123456" />
                </FG>
                <FG label="E-mail profissional">
                  <div style={{ position: 'relative' }}>
                    <Mail size={13} style={{ position: 'absolute', left: 9, top: 10, color: 'var(--text-muted)' }} />
                    <input type="email" value={form.professional_email} onChange={e => setForm(f => ({ ...f, professional_email: e.target.value }))} placeholder="advogado@escritorio.com" style={{ paddingLeft: 30 }} />
                  </div>
                </FG>
                <FG label="Telefone profissional">
                  <div style={{ position: 'relative' }}>
                    <Phone size={13} style={{ position: 'absolute', left: 9, top: 10, color: 'var(--text-muted)' }} />
                    <input value={form.professional_phone} onChange={e => setForm(f => ({ ...f, professional_phone: e.target.value }))} placeholder="Ex: (11) 99999-9999" style={{ paddingLeft: 30 }} />
                  </div>
                </FG>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '.75rem', borderTop: '1px solid var(--border-light)', paddingTop: '1rem' }}>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={loading}>
              <Check size={13} /> {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>

      </div>

      <MeuConsumoIA userId={user?.id} />
    </div>
  );
}
